import { Log } from '@aboutyou/legacy-shared'
import {
  BOLDERIZE_DOUBLE_REGEX,
  BOLDERIZE_DOUBLE_REGEX_PART,
  bolderize,
} from 'helpers/bolderize'
import { createCanvas } from 'helpers/canvas'
import { exists } from 'helpers/exists'
import React from 'react'
import styled, { css } from 'styled-components'
import { primaryColor } from 'uiComponents/constants/colors'
import {
  mediaDesktopAndUp,
  mediaMinXSBreakpoint,
  mediaTabletAndDown,
} from 'uiComponents/constants/customMedia'
import {
  fontFamilyRaw,
  fontWeightBoldRaw,
  fontWeightHeavyRaw,
  fontWeightMediumBoldRaw,
  fsMS,
  fsXXXL,
  fwBold,
  fwHeavy,
  fwMediumBold,
} from 'uiComponents/constants/typography'

// Looks a little weird with the variables
// But the EditorialHeadline can appear inside a large Virtualized List
// Which means its height needs to be calculated very fast
// and height should be available before rendering
//
// (CellMeasurer for example would calculate
// post-render and use expensive DOM-APIs,
// so we opted to calculate height with canvas instead)

const SPACING_BETWEEN = 25

const PADDING_LEFT_RIGHT = 25
const PADDING_TOP = 20
const PADDING_BOTTOM = 55

const MAX_WIDTH_FACTOR = 0.8
const MAX_WIDTH_PERCENT = MAX_WIDTH_FACTOR * 100

const LH_MS = 1.56
const LH_MS_PX = fsMS * LH_MS
const FONT_TEXT = `${fontWeightMediumBoldRaw} ${fsMS}px ${fontFamilyRaw}`
const FONT_TEXT_BOLDERIZED = `${fontWeightBoldRaw} ${fsMS}px ${fontFamilyRaw}`

const LH_XXXL = 1.3
const LH_XXXL_PX = fsXXXL * LH_XXXL
const FONT_HEADLINE = `${fontWeightHeavyRaw} ${fsXXXL}px ${fontFamilyRaw}`

const Wrapper = styled.section`
  display: flex;
  flex: 1 0 auto;
  padding: ${PADDING_TOP}px 0 ${PADDING_BOTTOM}px;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;

  @media ${mediaTabletAndDown} {
    padding-left: ${PADDING_LEFT_RIGHT}px;
    padding-right: ${PADDING_LEFT_RIGHT}px;
  }
`

const Headline = styled.h2<{ withText: boolean }>`
  font-size: ${fsXXXL}px;
  color: ${primaryColor};
  line-height: ${LH_XXXL};
  ${fwHeavy};

  ${({ withText }) =>
    withText &&
    css`
      margin-bottom: ${SPACING_BETWEEN}px;
    `};
`

const Text = styled.p`
  font-size: ${fsMS}px;
  line-height: ${LH_MS};
  ${fwMediumBold};

  & > strong {
    ${fwBold};
  }
`

export const TextContainer = styled.div`
  max-width: 100%;
  text-align: center;

  @media ${mediaDesktopAndUp} {
    max-width: ${MAX_WIDTH_PERCENT}%;
  }
`

interface TitleElement {
  title: string
  text: null
}
interface TextElement {
  title: null
  text: string
}
interface HeadlineElement {
  title: string
  text: string
}

type Props = {
  className?: string
} & (TitleElement | TextElement | HeadlineElement)

const EditorialHeadline: React.FC<Props> = ({ className, title, text }) => {
  const withText = exists(text)
  const bolderizedText = withText
    ? bolderize(text as string, { type: 'single' })
    : ''

  return (
    <Wrapper data-testid="EditorialHeadline" className={className}>
      <TextContainer>
        {title && (
          <Headline data-testid="title" withText={withText}>
            {title}
          </Headline>
        )}
        {bolderizedText && (
          <Text
            data-testid="text"
            dangerouslySetInnerHTML={{ __html: bolderizedText }}
          />
        )}
      </TextContainer>
    </Wrapper>
  )
}

export const calcHeight = ({
  title,
  text,
  listWidth,
  clientWidth,
}: {
  title: string | null
  text: string | null
  listWidth: number
  clientWidth: number
}) => {
  const isMin = clientWidth < mediaMinXSBreakpoint
  const leftRightPadding = isMin ? PADDING_LEFT_RIGHT * 2 : 0
  const rowWidth = isMin
    ? listWidth - leftRightPadding
    : listWidth * MAX_WIDTH_FACTOR

  const titleRowCount = title
    ? measureTextRowCount(title, 'title', rowWidth)
    : 0

  const textRowCount = text ? measureTextRowCount(text, 'text', rowWidth) : 0

  let height = titleRowCount * LH_XXXL_PX + textRowCount * LH_MS_PX

  if (title && text) {
    height += SPACING_BETWEEN
  }

  return Math.ceil(height + (PADDING_TOP + PADDING_BOTTOM))
}

export default EditorialHeadline

// ------------------------
// Height calculation for EditorialHeadline
// ------------------------
type TextType = 'text' | 'title'
type CtxType =
  | CanvasRenderingContext2D
  | OffscreenCanvasRenderingContext2D
  | null

let titleCtx: CtxType = null
let textCtx: CtxType = null

function getCanvas(textType: TextType) {
  if (process.browser && textType === 'title' && titleCtx === null) {
    try {
      titleCtx = createCanvas()
      titleCtx!.font = FONT_HEADLINE
    } catch (e) {
      if (process.env.NODE_ENV !== 'test') {
        Log.warn(e)
      }
    }
  }
  if (process.browser && textType === 'text' && textCtx === null) {
    try {
      textCtx = createCanvas()
      textCtx!.font = FONT_TEXT
    } catch (e) {
      if (process.env.NODE_ENV !== 'test') {
        Log.warn(e)
      }
    }
  }

  return textType === 'title' ? titleCtx : textCtx
}

function getTextParts(text: string) {
  const matches = text.match(BOLDERIZE_DOUBLE_REGEX)
  const cleanedMatches = matches
    ? matches.map(part => part.replace(BOLDERIZE_DOUBLE_REGEX_PART, ''))
    : []

  return text
    .split(BOLDERIZE_DOUBLE_REGEX)
    .filter(s => !!s)
    .map(part => ({
      text: part,
      isBold: cleanedMatches.includes(part),
    }))
}

function measureText(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  text: string,
) {
  // Have to round, because browsers seem to rounds in their rendering as well
  // Both, ceil and floor are less accurate here
  return Math.round(ctx.measureText(text).width)
}

const measureTextRowCount = (
  text: string,
  textType: 'title' | 'text',
  rowWidth: number,
) => {
  const ctx = getCanvas(textType)

  if (!ctx) {
    // NaN would be "correct", but then react-virtualized's "Grid" crashes,
    // so we try to take an arbitrary default
    return textType === 'title' ? 2 : 4
  }

  const separatorWidth = measureText(ctx, ' ')
  const textParts = getTextParts(text)

  let rowCount = 1
  let tempLength = 0

  for (const part of textParts) {
    const words = part.text.split(' ')
    let i = 0

    if (textType === 'text') {
      ctx.font = part.isBold ? FONT_TEXT_BOLDERIZED : FONT_TEXT
    }

    // Put word for word on a line and
    // increment rowCount when rowWidth is reached ("line break")
    while (i < words.length) {
      const textWidth = measureText(ctx, words[i])

      tempLength += (i === 0 ? 0 : separatorWidth) + textWidth

      if (tempLength >= rowWidth) {
        rowCount += 1
        tempLength = textWidth // Start again with the first word on next line
      }

      i++
    }
  }

  return rowCount
}
